<template>
  <div class="custom-node node-termination" :class="{ 'editing': isEditing }">
    <Handle type="target" :position="Position.Left" />

    <div class="node-content">
      <div class="node-icon">
        <i class="fas fa-flag-checkered"></i>
      </div>
      <div class="node-body">
        <div class="node-label">
          {{ props.data.label || 'The End' }}
        </div>
        <div v-if="props.data.endBonus !== undefined && props.data.endBonus !== null" class="node-bonus">
          <i class="fas fa-star me-1"></i>
          Bonus: {{ props.data.endBonus }} pts
        </div>
      </div>
      <button
        class="btn btn-sm btn-link text-white"
        @click.stop="toggleEdit"
        :title="isEditing ? 'Fermer' : 'Éditer'"
      >
        <i :class="isEditing ? 'fas fa-times' : 'fas fa-edit'"></i>
      </button>
    </div>

    <!-- Panneau d'édition dépliable -->
    <div v-if="isEditing" class="edit-panel" @click.stop>
      <div class="mb-2">
        <label class="form-label">Label</label>
        <input
          type="text"
          class="form-control form-control-sm"
          v-model="localLabel"
          placeholder="e.g. Success, Failed, Partial"
          @keyup.enter="saveChanges"
        />
        <small class="form-text text-muted">Nom de cette terminaison</small>
      </div>

      <div class="mb-3">
        <label class="form-label">End Bonus (points)</label>
        <input
          type="number"
          class="form-control form-control-sm"
          v-model.number="localBonus"
          placeholder="0"
          step="1"
          @keyup.enter="saveChanges"
        />
        <small class="form-text text-muted">Points bonus ajoutés à la fin du test</small>
      </div>

      <div class="d-flex gap-2">
        <button class="btn btn-sm btn-primary" @click="saveChanges">
          <i class="fas fa-check me-1"></i>Save
        </button>
        <button class="btn btn-sm btn-secondary" @click="cancelEdit">
          <i class="fas fa-times me-1"></i>Cancel
        </button>
      </div>
    </div>

    <button
      class="node-delete-btn"
      @click.stop="onDelete"
      title="Supprimer le nœud"
    >
      <i class="fas fa-times"></i>
    </button>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import { Handle, Position } from '@vue-flow/core'

const props = defineProps({
  id: String,
  data: Object
})

const emit = defineEmits(['delete', 'update'])

const isEditing = ref(false)
const localLabel = ref(props.data.label || 'The end')
const localBonus = ref(props.data.endBonus || 0)

console.log('🔄 TerminationNode loaded:', { id: props.id, label: props.data.label, endBonus: props.data.endBonus })

// Watch pour mettre à jour les valeurs locales quand les props changent
watch(() => props.data, (newData) => {
  if (!isEditing.value) {
    localLabel.value = newData.label || 'The end'
    localBonus.value = newData.endBonus || 0
    console.log('🔄 Props updated:', { label: newData.label, endBonus: newData.endBonus })
  }
}, { deep: true })

function toggleEdit() {
  isEditing.value = !isEditing.value
  if (isEditing.value) {
    localLabel.value = props.data.label || 'The end'
    localBonus.value = Number(props.data.endBonus) || 0
    console.log('✏️ Edit mode:', { localLabel: localLabel.value, localBonus: localBonus.value })
  }
}

function saveChanges() {
  isEditing.value = false
  const bonus = Number(localBonus.value) || 0
  emit('update', {
    id: props.id,
    data: {
      label: localLabel.value.trim() || 'The end',
      endBonus: bonus
    }
  })
  console.log('💾 TerminationNode saved:', { label: localLabel.value, endBonus: bonus })
}

function cancelEdit() {
  isEditing.value = false
  localLabel.value = props.data.label || 'The end'
  localBonus.value = props.data.endBonus || 0
}

function onDelete() {
  if (window.confirm('⚠️ Are you sure you want to delete this node? \nThis action cannot be undone.')) {
    emit('delete', { nodeId: props.id, elementId: props.data?.elementId })
  }
}
</script>

<style scoped>
.custom-node {
  position: relative;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  min-width: 200px;
  max-width: 280px;
  background: white;
  border: 2px solid;
  transition: all 0.3s ease;
}

.custom-node:hover {
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.node-termination {
  border-color: #ffc107;
  background: linear-gradient(135deg, #ffc107 0%, #ff9800 100%);
}

.node-content {
  padding: 12px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.node-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
  flex-shrink: 0;
}

.node-body {
  flex: 1;
  min-width: 0;
}

.node-label {
  color: white;
  font-weight: 600;
  font-size: 14px;
  word-wrap: break-word;
}

.node-bonus {
  color: white;
  font-size: 12px;
  margin-top: 4px;
  display: flex;
  align-items: center;
}

.edit-panel {
  background: white;
  padding: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.3);
  margin-top: 8px;
}

.edit-panel .form-label {
  font-size: 12px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
}

.edit-panel .form-control {
  font-size: 13px;
}

.edit-panel .form-text {
  font-size: 11px;
}

.custom-node.editing {
  min-width: 280px;
}

.node-delete-btn {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #dc3545;
  color: white;
  border: 2px solid white;
  display: none;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.custom-node:hover .node-delete-btn {
  display: flex;
}

.node-delete-btn:hover {
  background: #c82333;
  transform: scale(1.1);
}


.vue-flow__handle {
    width: 16px;
    height: 16px;
    background-color: #d67f2e ;
    border: 1px solid #fff;
    border-radius: 100%;

}
</style>
